type IQueryableField =
  | string
  | { attr: string }
  | { path: string[] }
  | { pathTo: string[]; attrs: string[] }

export function buildSearchFilter(attributes: IQueryableField[], query: string) {
  query = query.trim()
  if (!query) {
    return { value: true }
  }
  const words = query.split(/\s+/)
  const and = words.map(word => {
    const number = Number(word.match(/^£?([\d.,]+)$/)?.[1].replace(/,/g, ""))

    function condition(name: string, value: any) {
      return name.match(/pennies$/i)
        ? isNaN(number)
          ? { value: false }
          : { eq: [value, { value: Math.round(number * 100) }] }
        : { like: [value, { value: word }] }
    }

    return {
      or: attributes.map(attribute => {
        if (typeof attribute === "string") {
          return condition(attribute, { attr: attribute })
        } else if ("attr" in attribute) {
          return condition(attribute.attr, attribute)
        } else if ("path" in attribute) {
          const name = attribute.path[attribute.path.length - 1]
          return {
            anyIn: {
              attribute: attribute.path.slice(0, -1),
              query: condition(name, { attr: name }),
            },
          }
        } else if ("pathTo" in attribute) {
          return {
            anyIn: {
              attribute: attribute.pathTo,
              query: {
                or: attribute.attrs.map(attr => condition(attr, { attr })),
              },
            },
          }
        } else {
          return { value: false }
        }
      }),
    }
  })

  if (and.length === 1) {
    return and[0]
  } else {
    return { and }
  }
}

export function buildOrdersSearchFilter(query: string) {
  return buildSearchFilter(
    [
      "identifier",
      "purchase_order_reference",
      "sage_order_number",
      { path: ["quote", "identifier"] },
      { path: ["customer", "name"] },
      { path: ["user", "name"] },
      {
        pathTo: ["items", "product"],
        attrs: ["stock_code", "legacy_stock_code", "body"],
      },
    ],
    query,
  )
}

export function buildQuotesSearchFilter(query: string) {
  return buildSearchFilter(
    [
      "identifier",
      "customer_reference",
      { path: ["customer", "name"] },
      { path: ["user", "name"] },
    ],
    query,
  )
}

export function buildPurchaseOrdersSearchFilter(query: string) {
  return buildSearchFilter(
    [
      "identifier",
      "sage_order_number",
      { path: ["purchase_order_items", "product", "stock_code"] },
      { path: ["supplier", "name"] },
    ],
    query,
  )
}

export function buildSuppliersSearchFilter(query: string) {
  return buildSearchFilter(["name", "account_reference"], query)
}
export function buildSupplierMastersSearchFilter(query: string) {
  return buildSearchFilter(["name", { path: ["suppliers", "account_reference"] }], query)
}

export function buildProductsSearchFilter(query: string) {
  return buildSearchFilter(["stock_code", "legacy_stock_code", "body"], query)
}

export function buildProductGroupSearchFilter(query: string) {
  return buildSearchFilter(["description", { path: ["products", "stock_code"] }], query)
}
export function buildProductGroupMasterSearchFilter(query: string) {
  return buildSearchFilter(
    ["description", { path: ["product_groups", "products", "product_master", "stock_code"] }],
    query,
  )
}

export function buildCustomerSearchFilter(query: string) {
  return buildSearchFilter(["name", "account_reference"], query)
}

export function buildUsersSearchFilter(query: string) {
  return buildSearchFilter(["name", "skeem_email"], query)
}

export function buildDeliveriesSearchFilter(query: string) {
  return buildSearchFilter(
    [
      { path: ["order", "identifier"] },
      { path: ["order", "purchase_order_reference"] },
      { path: ["order", "quote", "identifier"] },
      { path: ["order", "customer", "name"] },
      { path: ["user", "name"] },
    ],
    query,
  )
}

export function buildJobsSearchFilter(query: string) {
  return buildSearchFilter(
    [
      "identifier",
      "address_line_1",
      "address_postcode",
      { path: ["order", "identifier"] },
      { path: ["customer", "name"] },
      { pathTo: ["items", "product"], attrs: ["stock_code", "legacy_stock_code", "body"] },
    ],
    query,
  )
}

export function buildOrderRequestsSearchFilter(query: string) {
  return buildSearchFilter(
    [
      "identifier",
      { path: ["purchase_order", "identifier"] },
      { path: ["order", "identifier"] },
      { pathTo: ["items", "source_product"], attrs: ["stock_code", "legacy_stock_code", "body"] },
      { pathTo: ["items", "target_product"], attrs: ["stock_code", "legacy_stock_code", "body"] },
    ],
    query,
  )
}

export function buildBrandsSearchFilter(query: string) {
  return buildSearchFilter(["name"], query)
}

export function buildCategoriesSearchFilter(query: string) {
  return buildSearchFilter(["name", "title"], query)
}
